import { DeviceModel, DeviceStatus } from '@/features/devices/types/types'
import ConnectIcon from '@/shared/assets/icons/connectIcon.svg?react'
import EditIcon from '@/shared/assets/icons/edit.svg?react'
import {
  BaseButton,
  BaseCheckbox,
  DeviceIcon,
  DeviceState,
  ItemLayout,
  StatusLabel,
} from '@/shared/components'
import { formatDate } from '@/shared/lib/date'
import { ColumnDef } from '@tanstack/react-table'
import { DeviceStatusIcons, DeviceStatusTitles } from '../types/constants'
import { getShortenId } from '@/shared/utils/getShortenId'
import { CopyBlock } from '@/shared/components/CopyBlock/CopyBlock'

interface RequisitColumnsProps {
  connectNewDevice: (key: string, qrCode: string) => void
  onEdit?: (id: string) => void
}

export const getDevicesColumns = ({
  connectNewDevice,
  onEdit,
}: RequisitColumnsProps): ColumnDef<DeviceModel>[] => [
  {
    id: 'select',
    accessorKey: 'select',
    header: ({ table }) => (
      <div
        onClick={(event) => {
          event.stopPropagation()
          event.preventDefault()
        }}
      >
        <BaseCheckbox
          checked={table.getIsAllPageRowsSelected()}
          onChange={(value) => table.toggleAllPageRowsSelected(value)}
        />
      </div>
    ),
    cell: ({ row }) => (
      <div
        onClick={(event) => {
          event.stopPropagation()
          event.preventDefault()
        }}
      >
        <BaseCheckbox
          checked={row.getIsSelected()}
          onChange={(value) => {
            row.toggleSelected(value)
          }}
        />
      </div>
    ),
    enableSorting: false,
    enableHiding: false,
  },
  {
    accessorKey: 'id',
    header: 'Реквизит',
    cell: ({ row }) => {
      return (
        <ItemLayout
          icon={<DeviceIcon status={DeviceStatusIcons[row.original.deviceStatus]} />}
          title={row.original.name}
          subtitle={
            <CopyBlock text={row.original?.id} color="secondary" title="row.original?.id">
              {getShortenId(row.original?.id)}
            </CopyBlock>
          }
        />
      )
    },
  },
  {
    accessorKey: 'status',
    header: 'Статус',
    cell: ({ row }) => {
      const status = row.original.deviceStatus
      return (
        <StatusLabel status={DeviceStatusIcons[status]}>{DeviceStatusTitles[status]}</StatusLabel>
      )
    },
  },
  {
    accessorKey: 'date',
    header: 'Последняя активность',
    cell: ({ row }) => {
      return (
        <div className="font-klein font-medium">
          {row.original.deviceStatus !== DeviceStatus.NotConnected && (
            <>
              <span className="mr-1 text-text-secondary">Запущено:</span>
              <span>
                {!row.original.lastPingedAt || row.original.lastPingedAt === 'Нет данных'
                  ? 'Нет данных'
                  : formatDate(row.original.lastPingedAt, 'long', {})}
              </span>
            </>
          )}
        </div>
      )
    },
  },
  {
    accessorKey: 'requisitesCount',
    header: 'Реквизиты',
    cell: ({ row }) => {
      return (
        <span className="font-klein font-medium">
          {row.original.deviceStatus !== DeviceStatus.NotConnected}
        </span>
      )
    },
  },
  {
    accessorKey: 'state',
    header: 'Состояние',
    cell: ({ row }) => {
      const status = row.original.deviceStatus
      if (status === DeviceStatus.NotConnected) {
        return (
          <BaseButton
            icon={<ConnectIcon />}
            radius="lg"
            className="!px-[22px] !py-[10px]"
            onClick={(event) => {
              event.stopPropagation()
              event.preventDefault()
              connectNewDevice(row.original.key, row.original.qrCode)
            }}
          >
            Подключить
          </BaseButton>
        )
      } else {
        const status = row.original.status

        return (
          <DeviceState
            status={status}
            direction="col"
            battery={row.original.battery}
            network={row.original.network}
          />
        )
      }
    },
  },
  {
    id: 'actions',
    header: '',
    cell: ({ row }) => {
      return (
        <div className="flex gap-3">
          <BaseButton
            variant="ghost"
            size="icon"
            onClick={(event) => {
              event.stopPropagation()
              event.preventDefault()
              onEdit?.(row.original.id)
            }}
          >
            <EditIcon className="text-text-secondary" />
          </BaseButton>
        </div>
      )
    },
  },
]
